import type * as XLSX from "xlsx-js-style";

type XlsxLibrary = typeof XLSX;

type WorksheetStyleOptions = {
  titleRows?: number[];
  headerRows?: number[];
  numberColumns?: number[];
  dateColumns?: number[];
  columnWidths?: number[];
  dataStartsAt?: number;
};

const blue = "0B5CAD";
const blueDark = "073E7A";
const blueLight = "EAF3FC";
const line = "D7E3EF";

const border = { bottom: { style: "thin" as const, color: { rgb: line } } };
const bodyStyle = { font: { name: "Arial", sz: 10, color: { rgb: "1E293B" } }, alignment: { vertical: "center" as const, wrapText: true }, border };
const alternateBodyStyle = { ...bodyStyle, fill: { patternType: "solid" as const, fgColor: { rgb: "F7FAFD" } } };
const headerStyle = { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid" as const, fgColor: { rgb: blue } }, alignment: { horizontal: "center" as const, vertical: "center" as const, wrapText: true }, border };
const titleStyle = { font: { name: "Arial", sz: 15, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid" as const, fgColor: { rgb: blueDark } }, alignment: { horizontal: "left" as const, vertical: "center" as const }, border };

function existingCell(sheet: XLSX.WorkSheet, XLSXLib: XlsxLibrary, row: number, column: number) {
  return sheet[XLSXLib.utils.encode_cell({ r: row, c: column })] as XLSX.CellObject | undefined;
}

export function applyWorksheetStyle(XLSXLib: XlsxLibrary, sheet: XLSX.WorkSheet, options: WorksheetStyleOptions = {}) {
  if (!sheet["!ref"]) return sheet;
  const range = XLSXLib.utils.decode_range(sheet["!ref"]);
  const titleRows = new Set(options.titleRows ?? []);
  const headerRows = new Set(options.headerRows ?? []);
  const dataStartsAt = options.dataStartsAt ?? Math.max(...(options.headerRows ?? [-1])) + 1;
  const numberColumns = new Set(options.numberColumns ?? []);
  const dateColumns = new Set(options.dateColumns ?? []);

  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const cell = existingCell(sheet, XLSXLib, row, column);
      if (!cell) continue;
      if (titleRows.has(row)) cell.s = titleStyle;
      else if (headerRows.has(row)) cell.s = headerStyle;
      else cell.s = row >= dataStartsAt && (row - dataStartsAt) % 2 === 1 ? alternateBodyStyle : bodyStyle;
      if (row >= dataStartsAt && numberColumns.has(column)) {
        cell.z = "#,##0.####";
        cell.s = { ...(cell.s ?? {}), alignment: { horizontal: "right", vertical: "center" }, numFmt: "#,##0.####" };
      }
      if (row >= dataStartsAt && dateColumns.has(column)) {
        cell.z = "dd/mm/yyyy hh:mm";
        cell.s = { ...(cell.s ?? {}), alignment: { horizontal: "center", vertical: "center" }, numFmt: "dd/mm/yyyy hh:mm" };
      }
    }
  }

  sheet["!cols"] = (options.columnWidths ?? []).map((wch) => ({ wch }));
  sheet["!rows"] = Array.from({ length: range.e.r + 1 }, (_, row) => ({ hpt: titleRows.has(row) ? 28 : headerRows.has(row) ? 24 : 20 }));
  if (headerRows.size) {
    const headerRow = Math.max(...headerRows);
    sheet["!autofilter"] = { ref: XLSXLib.utils.encode_range({ s: { r: headerRow, c: range.s.c }, e: range.e }) };
  }
  sheet["!margins"] = { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 };
  return sheet;
}

export function applyLabelStyle(XLSXLib: XlsxLibrary, sheet: XLSX.WorkSheet, rows: number[], columns: number[]) {
  for (const row of rows) {
    for (const column of columns) {
      const cell = existingCell(sheet, XLSXLib, row, column);
      if (!cell) continue;
      cell.s = {
        font: { name: "Arial", sz: 10, bold: true, color: { rgb: blueDark } },
        fill: { patternType: "solid", fgColor: { rgb: blueLight } },
        alignment: { vertical: "center", wrapText: true },
        border,
      };
    }
  }
  return sheet;
}

export function styleWorkbook(XLSXLib: XlsxLibrary, workbook: XLSX.WorkBook, title: string) {
  workbook.Props = {
    Title: title,
    Subject: "BOM System - Hệ thống quản lý định mức vật tư",
    Author: "Phòng Kế hoạch",
    Company: "Phòng Kế hoạch",
    CreatedDate: new Date(),
  };
  return workbook;
}
